<template>
  <DocumentsAISummaryMessageFromAI
    ref="summaryMessage"
    :message-date="documentSummary.createdAt"
    shouldDisplaySummaryMessage
  >

    <template v-if="$slots['before-summary']" #before-summary>
      <slot name="before-summary"></slot>
    </template>

    <AIAssistantText :text="documentSummary.summary" />

    <v-divider class="my-4" />

    <div class="d-flex column-gap-2">
      <app-material-icon name="info" :size="16" />
      <span class="text--x-small text--regular" style="color: #2A476A;">
        {{ $t('project.documents.ai-summary.DocumentsAISummaryRequestChatMessage.thisContentIsGeneratedByAI') }}
      </span>
    </div>

    <DocumentsAISummaryMessageActions
      v-if="areActionsDisplayed"
      :summary-feedback="summaryFeedback"
      @copy="copySummary"
      @downvote="downVoteSummary"
      @upvote="upVoteSummary"
    />
  </DocumentsAISummaryMessageFromAI>
</template>

<script lang="ts">
import { ComponentPublicInstance, defineComponent, PropType } from 'vue'
import { mapGetters } from 'vuex'

import AIAssistantText from '@/common/ai-assistant/AIAssistantText.vue'
import { pushToMatomo, ProtegeMatomoActions } from '@/common/analytics/matomo'
import { toast } from '@/common/utils/toast'
import { type DocumentAISummary, SummaryFeedback } from '@/project/documents/pane/DocumentsAISummaryPane.vue'
import aiSummariesService from '@/services/ai-summaries.service'

import DocumentsAISummaryMessageActions from './DocumentsAISummaryMessageActions.vue'
import DocumentsAISummaryMessageFromAI from './DocumentsAISummaryMessageFromAI.vue'

export default defineComponent({
  name: 'DocumentsAISummaryChatMessage',
  components: {
    DocumentsAISummaryMessageActions,
    DocumentsAISummaryMessageFromAI,
    AIAssistantText,
  },
  props: {
    documentSummary: {
      type: Object as PropType<DocumentAISummary>,
      required: true,
    },
    withTextAnimation: {
      type: Boolean,
      default: false,
    },
    displayActions: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      areActionsDisplayed: true,
      summaryFeedback: this.documentSummary.feedback,
      messageResizeObserver: null as ResizeObserver | null,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
  },
  mounted () {
    const summaryMessageComponent = this.$refs['summaryMessage'] as ComponentPublicInstance<typeof DocumentsAISummaryMessageFromAI>

    if (this.withTextAnimation && summaryMessageComponent) {
      this.messageResizeObserver = new ResizeObserver(this.scrollToSummaryEnd)
      this.messageResizeObserver.observe(summaryMessageComponent.$el)
    }
  },
  beforeUnmount () {
    if (this.messageResizeObserver) {
      this.messageResizeObserver.disconnect()
    }
  },
  methods: {
    async copySummary () {
      await navigator.clipboard.writeText(this.documentSummary.summary)

      pushToMatomo(
        'copy',
        ProtegeMatomoActions.copySummary,
        { source: 'summary result', foldertype: 'dataroom' },
      )
    },
    async upVoteSummary () {
      try {
        await aiSummariesService.patchAISummaryUpvote(this.roomMnemo, this.documentSummary.id)
        this.summaryFeedback = SummaryFeedback.FEEDBACK_POSITIVE

        pushToMatomo(
          'rate_feedback',
          ProtegeMatomoActions.feedback,
          { source: 'happy', foldertype: 'dataroom' },
        )
      } catch {
        toast.error(this.$t('common.msgFailErrorOccurred'))
      }
    },
    async downVoteSummary () {
      try {
        await aiSummariesService.patchAISummaryDownvote(this.roomMnemo, this.documentSummary.id)
        this.summaryFeedback = SummaryFeedback.FEEDBACK_NEGATIVE

        pushToMatomo(
          'rate_feedback',
          ProtegeMatomoActions.feedback,
          { source: 'sad', foldertype: 'dataroom' },
        )
      } catch {
        toast.error(this.$t('common.msgFailErrorOccurred'))
      }
    },
    async scrollToSummaryEnd () {
      await this.$nextTick()
      const summaryMessage = this.$refs['summaryMessage'] as ComponentPublicInstance<typeof DocumentsAISummaryMessageFromAI>
      (summaryMessage.$el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'end' })
    },
  },
})
</script>
